/*
 * Copyright (c) Nexign, JSC, 2019-2023.
 */
/**
 *
 * @author Evgenij Tyan
 * @author Yakov Gushchin
 * @author Anton Panasenko
 * @author Andrey Dubov
 * @version 1.23.0
 * @since 1.0.0
 */
import { createAction } from '../../../modules/app/createAction';
import {
    AUTH_SET_IS_FORBIDDEN,
    AUTH_SET_LOGGED_IN,
    AUTH_UNAUTHORIZED,
    LOAD_AUTH_DATA,
    LOAD_AUTH_DATA_FAILURE,
    LOAD_AUTH_DATA_SUCCESSFUL,
    LOGIN_PROCESS,
    LOGIN_PROCESS_ERROR,
    LOGIN_PROCESS_FAILED,
    LOGIN_PROCESS_SUCCESSFUL,
    LOGOUT_START,
    LOGOUT_START_FAILURE,
    LOGOUT_START_SUCCESSFUL,
    REDIRECT_TO_SOHO,
    REDIRECT_TO_SOHO_FAILURE,
} from '../constants/authConstants';
import {
    IAuthData,
    IAuthSetIsForbiddenPayload,
    IAuthSetIsLoggedInPayload,
    ILoadAuthData,
    ILoadAuthDataFailure,
    ILoadAuthDataSuccessful,
    ILoginProcess,
    ILoginProcessError,
    ILoginProcessRequest,
    ILoginProcessSuccessful,
    ILogoutStart,
    ILogoutStartFailure,
    ILogoutStartSuccessful,
    IRedirectToSoho,
    IRedirectToSohoFailure,
    TAuthSetIsForbiddenAction,
    TAuthSetIsLoggedInAction,
    TAuthUnauthorizedAction,
} from '../types/authTypes';
import { ILoginProcessFailed } from '../types/recoveryTypes';

/*
 * -------------------------------------------
 *         LOGIN PROCESS ACTIONS
 * -------------------------------------------
 */

/**
 * @description Action to start login process
 * @param {ILoginProcessRequest} payload - user login/password
 * @returns {ILoginProcess}
 */
export const loginProcessAction = (payload: ILoginProcessRequest): ILoginProcess =>
    createAction(LOGIN_PROCESS, payload);

/**
 * @description Successful login process
 * @param {ILoginProcessState} payload - login info
 * @returns {ILoginProcessSuccessful}
 */
export const loginProcessSuccessful = (payload: IAuthData): ILoginProcessSuccessful =>
    createAction(LOGIN_PROCESS_SUCCESSFUL, payload);
/**
 * @description Authorization error
 * @param {ILoginProcessState} payload - login info
 * @returns {loginProcessError}
 */
export const loginProcessError = (payload: string): ILoginProcessError => createAction(LOGIN_PROCESS_ERROR, payload);
/**
 * @description Failed login process
 * @param {string} message - error message
 * @returns {ILoginProcessFailed}
 */
export const loginProcessFailed = (payload: string): ILoginProcessFailed => createAction(LOGIN_PROCESS_FAILED, payload);

/*
 * -------------------------------------------
 *         LOAD AUTH DATA ACTIONS
 * -------------------------------------------
 */

export const loadAuthData = (): ILoadAuthData => createAction(LOAD_AUTH_DATA);

export const loadAuthDataSuccessful = (payload: IAuthData): ILoadAuthDataSuccessful =>
    createAction(LOAD_AUTH_DATA_SUCCESSFUL, payload);

export const loadAuthDataFailure = (payload: string): ILoadAuthDataFailure =>
    createAction(LOAD_AUTH_DATA_FAILURE, payload);

/*
 * -------------------------------------------
 *         LOGOUT ACTIONS
 * -------------------------------------------
 */

export const logoutStartAction = (): ILogoutStart => createAction(LOGOUT_START);
export const logoutStartSuccessful = (): ILogoutStartSuccessful => createAction(LOGOUT_START_SUCCESSFUL);
export const logoutStartFailure = (payload: string): ILogoutStartFailure => createAction(LOGOUT_START_FAILURE, payload);

export const redirectToSohoAction = (): IRedirectToSoho => createAction(REDIRECT_TO_SOHO);
export const redirectToSohoFailure = (payload: string): IRedirectToSohoFailure =>
    createAction(REDIRECT_TO_SOHO_FAILURE, payload);

export const authSetLoggedIn = (payload: IAuthSetIsLoggedInPayload): TAuthSetIsLoggedInAction =>
    createAction(AUTH_SET_LOGGED_IN, payload);

export const authSetIsForbidden = (payload: IAuthSetIsForbiddenPayload): TAuthSetIsForbiddenAction =>
    createAction(AUTH_SET_IS_FORBIDDEN, payload);

/*
 * -------------------------------------------
 *         UNAUTHORIZED
 * -------------------------------------------
 */

export const authUnauthorizedAction = (): TAuthUnauthorizedAction => createAction(AUTH_UNAUTHORIZED);
